import {
  MenuItem,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import useApi from "../../hooks/useApi";
import { Search, Plus, Users, UserRoundPlus, X } from "lucide-react";
import Modal from "../common/CommonModal";
import CreditCustomerList from "../common/CreditCustomerList";
import CashCustomerList from "../salesInvoice/CashCustomerList";
import RegisterWalkinCustomerForm from "../salesInvoice/RegisterWalkinCustomer";

const SampleIssueGeneral = ({
  WorkOrderData,
  setWorkOrderData,
  drAccount,
  setDrAccount,
  drDesc,
  setDrDesc,
  crAccount,
  setCrAccount,
  crDesc,
  setCrDesc,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT_NO,
  type = "SAMPLEISSUE",
  isDirectEntry = true,
  mode = "new",
  cashAccount,
  cashAccountDesc,
}) => {
  const { request } = useApi();
  const [salesmanList, setSalesmanList] = useState([]);
  const [coordinatorList, setCoordinatorList] = useState([]);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [activeAccountType, setActiveAccountType] = useState(null);
  const isSampleReturn = type === "SAMPLERETURN";

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    customer: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const paymentType = WorkOrderData?.paymentType || "cash";

  const displayDrAccount = isSampleReturn ? crAccount : drAccount;
  const displayDrDesc = isSampleReturn ? crDesc : drDesc;

  const displayCrAccount = isSampleReturn ? drAccount : crAccount;
  const displayCrDesc = isSampleReturn ? drDesc : crDesc;

  useEffect(() => {
    const fetchList = async (url, setter) => {
      try {
        const response = await request({
          url,
          method: "GET",
          timeout: 10000,
        });
        setter(response || []);
      } catch (err) {
        console.error("Fetch error:", err);
        setter([]);
      }
    };

    fetchList(
      `${BASE_URL}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${DEPARTMENT_NO}`,
      setSalesmanList,
    );

    fetchList(
      `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/CORDMASTERLIST/-/-`,
      setCoordinatorList,
    );
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT_NO]);

  const { request: requestCashCustomers, loading: loadingCashCustomers } = useApi();
  const fetchCashCustomers = async (query = "-") => {
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/WALKINLIST/${DEPARTMENT_NO}/ALL/-/${query}`;
    try {
      const response = await requestCashCustomers({
        url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch cash customers", err);
      return [];
    }
  };

  const { request: requestCreditCustomers, loading: loadingCreditCustomers } = useApi();
  const fetchCreditCustomers = async (query = "-") => {
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/CUSTOMERS/-/${query}`;
    try {
      const response = await requestCreditCustomers({
        url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch credit customers", err);
      return [];
    }
  };

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [paymentType]);

  const fetchSuggestions = useCallback(async () => {
    const freetext = searchQuery.trim() || "-";

    if (paymentType === "cash" && searchQuery) {
      setSuggestions([]);
      const result = await fetchCashCustomers(freetext);
      setSuggestions(result || []);
      setShowSuggestions(true);
    } else if (paymentType === "credit" && searchQuery) {
      setSuggestions([]);
      const result = await fetchCreditCustomers(freetext);
      setSuggestions(result || []);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, paymentType]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchSuggestions();
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [fetchSuggestions]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchQuery("");
  }, [paymentType]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        if (searchInputRef.current && document.activeElement === searchInputRef.current) {
          e.preventDefault();
          if (paymentType === "credit") {
            setModalState({ isOpen: true, type: "cr-list", customer: null });
          } else if (paymentType === "cash") {
            setModalState({ isOpen: true, type: "cash-list", customer: null });
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [paymentType]);

  const handleCustomerSelect = (customer) => {
    const isCash = paymentType === "cash";
    const name = customer.DESCRIPTION || customer.contactName || customer.name || "";
    const acc = isCash ? (cashAccount || "") : (customer.ACCOUNT || "");
    setWorkOrderData({
      ...WorkOrderData,
      customer: name,
      customerAcc: acc,
    });
    setSearchQuery("");
    setLastSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    closeModal();
  };

  const handleCustomerList = () => {
    if (paymentType === "credit") {
      setModalState({ isOpen: true, type: "cr-list", customer: null });
    } else {
      setModalState({ isOpen: true, type: "cash-list", customer: null });
    }
  };

  const handleAddCustomer = () => {
    setLastSearchQuery(searchQuery);
    setModalState({ isOpen: true, type: "add", customer: { contactName: searchQuery } });
  };

  const { request: requestSaveCustomer } = useApi();


  const handleSaveCustomer = async (customerData) => {
    console.log("OnSave", customerData);
    try {
      const url = `${BASE_URL}/api/CRMAcmastCRM`;
      const response = await requestSaveCustomer({
        url: url,
        method: "POST",
        data: customerData,
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        timeout: 5000,
      });

      console.log("Customer Saved Sucessfully", response);
      closeModal();
    } catch (error) {
      console.error(
        "Upload Error:",
        error.response ? error.response.data : error.message,
      );
    }
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: "", customer: null });
    setSearchQuery(lastSearchQuery);
    setTimeout(() => {
      if (lastSearchQuery) fetchSuggestions();
    }, 0);
  };

  const handleAccountSelect = (account) => {
    if (activeAccountType === "dr") {
      setDrAccount(account.ACCOUNT || "");
      setDrDesc(account.DESCRIPTION || "");
    } else if (activeAccountType === "cr") {
      setCrAccount(account.ACCOUNT || "");
      setCrDesc(account.DESCRIPTION || "");
    }
    setIsAccountModalOpen(false);
    setActiveAccountType(null);
  };

  const handleKeyDown = useCallback(
    (e, type) => {
      if (e.key === "F2" && isDirectEntry && mode !== "view") {
        setActiveAccountType(type);
        setIsAccountModalOpen(true);
      }
    },
    [isDirectEntry, mode],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-3">
      <div className="lg:col-span-4 col-span-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Dm Iss:</span>
            <span className="text-red-600 font-bold text-base leading-tight">
              {WorkOrderData?.documentNo || "-"}
            </span>
          </div>
          <TextField
            label="Date"
            type="date"
            variant="outlined"
            size="small"
            value={WorkOrderData?.date || new Date().toISOString().split("T")[0]}
            onChange={(e) =>
              setWorkOrderData({ ...WorkOrderData, date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: "140px", "& .MuiInputBase-root": { height: "36px" } }}
          />
        </div>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Dr Account"
              value={displayDrAccount}
              onKeyDown={(e) => handleKeyDown(e, isSampleReturn ? "cr" : "dr")}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: isDirectEntry && mode !== "view" && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setActiveAccountType(isSampleReturn ? "cr" : "dr");
                          setIsAccountModalOpen(true);
                        }}
                      >
                        <Search size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="DR Description"
              value={displayDrDesc}
              sx={{ mt: 1 }}
              slotProps={{ input: { readOnly: true } }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Cr Account"
              value={displayCrAccount}
              onKeyDown={(e) => handleKeyDown(e, isSampleReturn ? "dr" : "cr")}
              slotProps={{
                input: {
                  readOnly: !isDirectEntry || mode === "view",
                  endAdornment: isDirectEntry && mode !== "view" && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setActiveAccountType(isSampleReturn ? "dr" : "cr");
                          setIsAccountModalOpen(true);
                        }}
                      >
                        <Search size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(e) =>
                isDirectEntry &&
                (isSampleReturn ? setDrAccount(e.target.value) : setCrAccount(e.target.value))
              }
            />
            <TextField
              fullWidth
              size="small"
              label="CR Description"
              value={displayCrDesc}
              sx={{ mt: 1 }}
              slotProps={{ input: { readOnly: true } }}
            />
          </Grid>
        </Grid>
      </div>

      <div className="lg:col-span-4 col-span-1">
        <div className="grid grid-cols-2 gap-2">
          <TextField
            select
            label="Sales Person"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.salespersonId || ""}
            onChange={(e) => {
              const selectedSalesman = salesmanList.find((s) => s.EMP_ID === e.target.value);
              setWorkOrderData({
                ...WorkOrderData,
                salespersonId: e.target.value,
                salesperson: selectedSalesman?.NAME || "",
              });
            }}
          >
            {salesmanList?.map((person) => (
              <MenuItem key={person.EMP_ID} value={person.EMP_ID}>
                {person.NAME}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Coordinator"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.cordinatorid || ""}
            onChange={(e) => {
              const selectedCoord = coordinatorList.find((c) => c.code === e.target.value);
              setWorkOrderData({
                ...WorkOrderData,
                cordinatorid: e.target.value,
                cordinatorName: selectedCoord?.Salesperson_Name || "",
              });
            }}
          >
            {coordinatorList?.map((coord, index) => (
              <MenuItem key={coord.code} value={coord.code}>
                {coord.Salesperson_Name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={isDirectEntry ? "Ref No" : "Proposal No"}
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.proposalNo || ""}
            onChange={(e) =>
              setWorkOrderData({ ...WorkOrderData, proposalNo: e.target.value })
            }
            slotProps={{ input: { readOnly: !isDirectEntry || mode === "view" } }}
          />
          <TextField
            label={"Project"}
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.projectId || ""}
            onChange={(e) =>
              setWorkOrderData({ ...WorkOrderData, projectId: e.target.value })
            }
          />
        </div>
      </div>

      <div className="lg:col-span-4 col-span-2">
        {mode !== "view" && (
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <RadioGroup
              row
              value={paymentType}
              onChange={(e) => {
                const val = e.target.value;
                setWorkOrderData({
                  ...WorkOrderData,
                  paymentType: val,
                  customer: "",
                  customerAcc: val === "cash" ? (cashAccount || "") : "",
                });
                setSearchQuery("");
                setSuggestions([]);
                setShowSuggestions(false);
              }}
            >
              <FormControlLabel
                value="cash"
                control={<Radio size="small" />}
                label="Walk-In"
                checked={paymentType === "cash"}
              />
              <FormControlLabel
                value="credit"
                control={<Radio size="small" />}
                label="Registered"
                checked={paymentType === "credit"}
              />
            </RadioGroup>
          </FormControl>
        )}

        <div className="relative">
          <div className="rounded border border-gray-300 overflow-hidden w-full shadow-sm bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <div className="flex items-center divide-x divide-gray-300">
              <div className="bg-gray-100 px-3 py-2 flex items-center">
                <Search className="text-gray-600" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                readOnly={mode === "view"}
                placeholder={
                  paymentType === "cash"
                    ? "Type to search or Add a new customer"
                    : "Type to search for a customer or Press F2 "
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-2 px-3 text-sm bg-white outline-none transition"
              />
              {mode !== "view" && (
                [paymentType === "cash" ? (
                  <button
                    key="add-btn"
                    title="Add new Customer"
                    onClick={handleAddCustomer}
                    className="bg-gray-50 px-3 py-2 transition text-gray-600 hover:text-gray-700 flex items-center"
                  >
                    <UserRoundPlus className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    key="list-btn"
                    title="Show Customer List"
                    onClick={handleCustomerList}
                    className="bg-gray-50 px-3 py-2 transition text-blue-600 hover:text-blue-700 flex items-center"
                  >
                    <Users className="w-5 h-5" />
                  </button>
                )]
              )}
            </div>
          </div>

          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-md shadow-lg z-10 max-h-52 overflow-y-auto">
              {loadingCreditCustomers || loadingCashCustomers ? (
                <div className="px-3 py-4 text-center text-gray-500">Loading...</div>
              ) : (
                <>
                  {suggestions?.map((customer, index) => (
                    <div
                      key={paymentType === "cash" ? customer.AutoId : customer.ACCOUNT}
                      onClick={() => handleCustomerSelect(customer)}
                      className="px-3 py-2 flex items-center justify-between cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-100"
                    >
                      <div className="font-medium text-gray-900 text-sm line-clamp-2">
                        {customer.DESCRIPTION}
                      </div>
                      <div className="text-sm text-white px-2 py-0.5 rounded-xl bg-blue-500">
                        {paymentType === "cash" ? customer.AutoId : customer.ACCOUNT}
                      </div>
                    </div>
                  ))}

                  {paymentType === "credit" && !suggestions?.length && (
                    <div className="px-3 py-2 text-gray-400 text-center border-t border-gray-200">
                      No Customer Found
                    </div>
                  )}

                  {paymentType === "cash" && showSuggestions && searchQuery.trim() !== "" && (!suggestions || suggestions.length === 0) && (
                    <div
                      onClick={handleAddCustomer}
                      className="px-3 py-3 text-blue-600 hover:text-blue-800 hover:bg-stone-50 flex items-center justify-center gap-2 cursor-pointer font-medium border-t border-gray-200"
                    >
                      <Plus size={20} />
                      Add New Customer
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {WorkOrderData?.customer && (
          <div className="bg-gradient-to-r from-slate-100 to-gray-100 border border-gray-200 rounded-lg mt-2 p-3 shadow-sm relative">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-base text-teal-700 line-clamp-2">
                  {WorkOrderData.customer}
                </h4>
              </div>
              <div className="flex items-center gap-1 px-1.5 py-1 bg-blue-600 rounded-full font-medium flex-shrink-0">
                <span className="text-white text-sm">Account:</span>
                <span className="text-blue-600 bg-white px-2 py-1 rounded-full text-xs font-semibold">
                  {/* CHANGED HERE: Reading dynamically from customerAcc state rather than hardcoding cashAccount */}
                  {WorkOrderData.customerAcc || "-"}
                </span>
              </div>
            </div>
            {mode !== "view" && (
              <button
                onClick={() => {
                  setWorkOrderData({
                    ...WorkOrderData,
                    customer: "",
                    customerAcc: paymentType === "cash" ? (cashAccount || "") : "",
                  });
                  setSearchQuery("");
                }}
                className="absolute -top-1.5 border -right-1.5 text-gray-400 hover:text-gray-500 bg-white hover:bg-gray-50 p-1 rounded-full transition-all duration-200"
                title="Remove Selected customer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Account Selection Modal */}
      <Modal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setIsAccountModalOpen(false);
          setActiveAccountType(null);
        }}
        title={`Select ${activeAccountType === "dr" ? "Debit Account" : "Credit Account"}`}
        size="xxl"
      >
        <CreditCustomerList
          handleCustomerSelect={handleAccountSelect}
          showSidebar={true}
          defaultType="All"
        />
      </Modal>

      {/* Customer Selection Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.type === "add"
            ? "Add New Customer"
            : modalState.type === "edit"
              ? "Edit Customer"
              : "Select Customer"
        }
        size="xxl"
        header={modalState.type === "add" ? false : true}
      >
        {modalState.type === "add" && (
          <RegisterWalkinCustomerForm
            onSave={handleSaveCustomer}
            onCancel={closeModal}
            areaCodes={[]}
            customer={modalState.customer}
          />
        )}
        {modalState.type === "cash-list" && (
          <CashCustomerList handleCustomerSelect={handleCustomerSelect} />
        )}
        {modalState.type === "cr-list" && (
          <CreditCustomerList
            handleCustomerSelect={handleCustomerSelect}
            defaultType="Customers"
          />
        )}
      </Modal>
    </div>
  );
};

export default SampleIssueGeneral;